import React, { useEffect, useState } from 'react';
import Card from './Card';
import {
  fetchNeisMealToday,
  type NeisMealDay,
  type NeisMealItem,
} from '../../utils/neis';

interface Props {
  officeCode?: string | null;
  schoolCode?: string | null;
  width?: string | number;
  style?: React.CSSProperties;
}

function formatDisplayDate(ymd: string): string {
  if (!ymd || ymd.length !== 8) return ymd;
  const month = Number(ymd.slice(4, 6));
  const day = Number(ymd.slice(6, 8));
  const date = new Date(Number(ymd.slice(0, 4)), month - 1, day);
  const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
  return `${month}월 ${day}일 (${weekdays[date.getDay()]})`;
}

const MealSection: React.FC<{ meal: NeisMealItem }> = ({ meal }) => (
  <div
    style={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--gap-level-3)',
    }}
  >
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: '8px',
      }}
    >
      <div
        style={{
          color: 'var(--Color-text-normal)',
          fontSize: '16px',
          fontFamily: 'var(--font-family-primary)',
          fontWeight: 600,
          lineHeight: '24px',
        }}
      >
        {meal.type}
      </div>
      {meal.calorie && (
        <div
          style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: '12px',
            fontFamily: 'var(--font-family-primary)',
            fontWeight: 400,
            lineHeight: '18px',
            whiteSpace: 'nowrap',
          }}
        >
          {meal.calorie}
        </div>
      )}
    </div>
    <ul
      style={{
        margin: 0,
        padding: 0,
        listStyle: 'none',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        columnGap: '16px',
        rowGap: '4px',
        width: '100%',
      }}
    >
      {meal.dishes.map((dish) => (
        <li
          key={`${meal.type}-${dish}`}
          style={{
            color: 'var(--Color-text-normal)',
            fontSize: '14px',
            fontFamily: 'var(--font-family-primary)',
            fontWeight: 400,
            lineHeight: '21px',
            minWidth: 0,
            overflowWrap: 'anywhere',
          }}
        >
          · {dish}
        </li>
      ))}
    </ul>
  </div>
);

const TodayMealCard: React.FC<Props> = ({
  officeCode,
  schoolCode,
  width = '411px',
  style,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mealDay, setMealDay] = useState<NeisMealDay | null>(null);

  const trimmedOffice = officeCode?.trim() || '';
  const trimmedSchool = schoolCode?.trim() || '';
  const hasCodes = Boolean(trimmedOffice && trimmedSchool);

  useEffect(() => {
    if (!hasCodes) {
      setMealDay(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchNeisMealToday({
      officeCode: trimmedOffice,
      schoolCode: trimmedSchool,
    })
      .then((result) => {
        if (cancelled) return;
        setMealDay(result);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('오늘 급식 조회 실패:', err);
        setMealDay(null);
        setError(err instanceof Error ? err.message : '급식 정보를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasCodes, trimmedOffice, trimmedSchool]);

  const body = (() => {
    if (!hasCodes) {
      return (
        <div
          style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: '14px',
            fontFamily: 'var(--font-family-primary)',
            lineHeight: '21px',
          }}
        >
          학교 나이스 코드가 설정되지 않았습니다.
        </div>
      );
    }
    if (loading) {
      return (
        <div
          style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: '14px',
            fontFamily: 'var(--font-family-primary)',
            lineHeight: '21px',
          }}
        >
          급식 정보를 불러오는 중...
        </div>
      );
    }
    if (error) {
      return (
        <div
          style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: '14px',
            fontFamily: 'var(--font-family-primary)',
            lineHeight: '21px',
          }}
        >
          {error}
        </div>
      );
    }
    if (!mealDay || mealDay.meals.length === 0) {
      return (
        <div
          style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: '14px',
            fontFamily: 'var(--font-family-primary)',
            lineHeight: '21px',
          }}
        >
          오늘 등록된 급식 정보가 없습니다.
        </div>
      );
    }
    return (
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--gap-level-4)',
        }}
      >
        {mealDay.meals.map((meal) => (
          <MealSection key={meal.type} meal={meal} />
        ))}
      </div>
    );
  })();

  return (
    <Card
      data-block="today-meal"
      width={width}
      padding="16px 20px"
      alignItems="flex-start"
      gap="16px"
      style={{
        display: 'flex',
        boxSizing: 'border-box',
        flexShrink: 0,
        flexDirection: 'column',
        minHeight: 'auto',
        ...style,
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        <div
          style={{
            color: 'var(--Color-text-normal, #161616)',
            fontSize: '18px',
            fontFamily: 'var(--font-family-primary)',
            fontWeight: 600,
            lineHeight: '27px',
          }}
        >
          오늘 급식
        </div>
        <div
          style={{
            color: 'var(--Color-text-assistive, #989898)',
            fontSize: '14px',
            fontFamily: 'var(--font-family-primary)',
            fontWeight: 400,
            lineHeight: '21px',
          }}
        >
          {mealDay?.date ? formatDisplayDate(mealDay.date) : '나이스 급식 정보'}
        </div>
      </div>
      {body}
    </Card>
  );
};

export default TodayMealCard;
